import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'core/store';
import {
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonPopover,
  IonSpinner,
  IonText,
  IonToolbar
} from '@ionic/react';
import { createOutline, ellipsisHorizontal } from 'ionicons/icons';
import Close from 'assets/icons/close.svg';
import PaintIcon from 'assets/icons/paint.svg';
import { UpdateProductBody } from 'core/types';
import { fetchProductDetails } from 'features/shared/inventory/store/InventoryActions';
import { saveProductUpdate } from 'features/shared/inventory/utils/saveProductUpdate';
import { hideSelectionDetailsModal } from 'features/shared/inventory/store/InventorySlice';
import InventoryDetailSkeletonLoader from 'features/shared/inventory/components/InventoryDetailSkeletonLoader';
import { displayError, displaySuccess } from 'core/services/AlertActions';
import {
  formatHexLabel,
  normalizeInventoryHex,
  rgbDisplayFromHex
} from 'features/shared/inventory/selections/color-selections/utils/inventoryHexDisplay';
import './ColorSelectionDetailsMobile.scss';

const na = (val?: string | null) =>
  val && val.trim() !== '' ? val.trim() : 'N/A';

const ColorSelectionDetailsMobile: React.FC = () => {
  const dispatch = useDispatch();
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { product_id, product, productDetailsLoading, updateProductLoading } =
    useSelector((state: RootState) => state.inventory);
  const [isEditing, setIsEditing] = useState(false);
  const [editLocation, setEditLocation] = useState('');
  const [editHex, setEditHex] = useState('');

  useEffect(() => {
    if (!homeId || !product_id) {
      return;
    }
    dispatch(fetchProductDetails(homeId, product_id));
  }, [dispatch, homeId, product_id]);

  // Follows the in-flight `editHex` while editing, not the saved value: the
  // swatch renders above (outside) the edit branch, so keying it on
  // product.hex_value meant dragging the color picker updated the small picker
  // chip and the RGB readout while the big swatch stayed on the old color —
  // reading as "my change didn't take". normalizeInventoryHex returns falsy for
  // partial input, so the PaintIcon fallback still degrades correctly mid-typing.
  const hexForSwatch = normalizeInventoryHex(
    isEditing ? editHex : product?.hex_value
  );

  // Exact same field set/labels as ColorSelectionDetailsDesktop.tsx's
  // attributes — no Description/Category fields, matching desktop verbatim.
  // colorGroupLabel is also exposed for the edit-mode read-only row below,
  // same as desktop's colorGroupLabel.
  const { attributes, colorGroupLabel } = useMemo(() => {
    if (!product) {
      return { attributes: [] as { label: string; value: string }[] };
    }
    const mfr = product.product_template.manufacturer;
    const serial = String(
      product.serial || product.product_template.model || ''
    );
    const templateName = (product.product_template.name || '').trim();
    const colorGroup = templateName || product.product_template.type;

    return {
      colorGroupLabel: colorGroup,
      attributes: [
        { label: 'Brand/Manufacturer', value: na(mfr?.name) },
        {
          label: 'Location',
          value: na(product.name || product.product_template.name)
        },
        { label: 'Color Code', value: na(serial) },
        { label: 'Color Group', value: na(colorGroup) },
        { label: 'Hex', value: formatHexLabel(product.hex_value) },
        { label: 'RGB', value: rgbDisplayFromHex(product.hex_value) }
      ]
    };
  }, [product]);

  const close = () => dispatch(hideSelectionDetailsModal());

  // Only Location and Hex are actually PATCH-able on a product instance —
  // Brand/Manufacturer lives on the catalog (product_template) and is
  // immutable here without re-pointing product_template_id, which this edit
  // form deliberately doesn't expose. Color Code (serial) is also read-only
  // here per explicit instruction, even though the API allows it.
  const startEditing = () => {
    if (!product) return;
    setEditLocation(product.name || '');
    setEditHex(normalizeInventoryHex(product.hex_value) || '');
    setIsEditing(true);
  };

  const saveEdits = async () => {
    if (!homeId || !product) return;
    if (editHex.trim() && !normalizeInventoryHex(editHex)) {
      dispatch(displayError('Enter a valid hex color (e.g. #3A7BD5).'));
      return;
    }
    const body: UpdateProductBody = {};
    if (editLocation !== (product.name || '')) {
      body.name = editLocation;
    }
    const normalizedHex = normalizeInventoryHex(editHex);
    if (normalizedHex !== normalizeInventoryHex(product.hex_value)) {
      body.attributes = { hex_value: normalizedHex };
    }
    const { ok, changed } = await saveProductUpdate(
      dispatch,
      homeId,
      product.id,
      body
    );
    // Stay in edit mode on failure so the user's edits survive to be retried —
    // createApiRequest resolves normally on a failed PATCH and has already
    // shown the real error message.
    if (!ok) return;
    if (changed) {
      dispatch(displaySuccess('Color Updated', 'Success'));
    }
    setIsEditing(false);
  };

  return (
    <IonPage className="hk-mobile-color-selection-details-page">
      <IonButton
        className="hk-mobile-color-selection-details-close-btn"
        fill="clear"
        shape="round"
        onClick={close}
      >
        <IonIcon icon={Close} />
      </IonButton>
      {productDetailsLoading ? (
        <InventoryDetailSkeletonLoader />
      ) : (
        <>
          {!!product && !isEditing && (
            <IonFab
              vertical="top"
              horizontal="start"
              className="hk-mobile-color-selection-details-controls-fab"
            >
              <IonFabButton
                className="hk-mobile-color-selection-details-controls-button"
                id="hk-mobile-color-selection-actions-trigger"
              >
                <IonIcon color="dark" icon={ellipsisHorizontal} />
              </IonFabButton>
              <IonPopover
                trigger="hk-mobile-color-selection-actions-trigger"
                triggerAction="click"
                dismissOnSelect={true}
                className="hk-mobile-color-selection-details-actions-popover"
              >
                <IonList
                  lines="full"
                  className="hk-mobile-color-selection-details-actions-list"
                >
                  <IonItem button detail={false} onClick={startEditing}>
                    <IonIcon slot="start" icon={createOutline} />
                    <IonLabel>Edit Details</IonLabel>
                  </IonItem>
                </IonList>
              </IonPopover>
            </IonFab>
          )}
          <IonContent className="hk-mobile-color-selection-details-content">
            <div className="hk-mobile-color-selection-details-image-section">
              <div
                className="hk-mobile-color-selection-details-swatch"
                style={
                  hexForSwatch ? { backgroundColor: hexForSwatch } : undefined
                }
              >
                {!hexForSwatch && (
                  <IonIcon
                    icon={PaintIcon}
                    className="hk-mobile-color-selection-details-swatch-icon"
                  />
                )}
              </div>
            </div>

            {isEditing && product ? (
              <div className="hk-mobile-color-selection-details-body">
                <div className="hk-mobile-color-selection-details-section-header">
                  <span className="hk-mobile-color-selection-details-section-title">
                    Color Details
                  </span>
                </div>
                <div className="hk-mobile-color-selection-details-edit-fields">
                  <div className="hk-mobile-color-selection-details-field">
                    <span className="hk-mobile-color-selection-details-field-label">
                      Brand/Manufacturer
                    </span>
                    <div className="hk-mobile-color-selection-details-field-value">
                      <span>
                        {na(product.product_template.manufacturer?.name)}
                      </span>
                    </div>
                  </div>
                  <div className="hk-inventory-edit-field">
                    <IonText color="medium">Location</IonText>
                    <IonInput
                      className="hk-inventory-edit-input"
                      fill="outline"
                      value={editLocation}
                      placeholder="Enter location"
                      onIonInput={(e) => setEditLocation(e.detail.value || '')}
                    />
                  </div>
                  <div className="hk-mobile-color-selection-details-field">
                    <span className="hk-mobile-color-selection-details-field-label">
                      Color Code
                    </span>
                    <div className="hk-mobile-color-selection-details-field-value">
                      <span>{na(product.serial)}</span>
                    </div>
                  </div>
                  <div className="hk-mobile-color-selection-details-field">
                    <span className="hk-mobile-color-selection-details-field-label">
                      Color Group
                    </span>
                    <div className="hk-mobile-color-selection-details-field-value">
                      <span>{na(colorGroupLabel)}</span>
                    </div>
                  </div>
                  <div className="hk-inventory-edit-field">
                    <IonText color="medium">Hex</IonText>
                    <div className="hk-color-selection-hex-field-row">
                      <input
                        type="color"
                        className="hk-color-selection-hex-swatch"
                        aria-label="Pick a color"
                        value={
                          normalizeInventoryHex(editHex)?.toLowerCase() ||
                          '#9ca8b6'
                        }
                        onChange={(e) => setEditHex(e.target.value)}
                      />
                      <IonInput
                        className="hk-inventory-edit-input"
                        fill="outline"
                        value={editHex}
                        placeholder="#RRGGBB"
                        onIonInput={(e) => setEditHex(e.detail.value || '')}
                      />
                    </div>
                  </div>
                  <div className="hk-mobile-color-selection-details-field">
                    <span className="hk-mobile-color-selection-details-field-label">
                      RGB
                    </span>
                    <div className="hk-mobile-color-selection-details-field-value">
                      <span>{rgbDisplayFromHex(editHex)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : !product ? (
              <div className="hk-mobile-color-selection-details-empty">
                <IonText color="medium">Item not found.</IonText>
              </div>
            ) : (
              <div className="hk-mobile-color-selection-details-body">
                <div className="hk-mobile-color-selection-details-section-header">
                  <span className="hk-mobile-color-selection-details-section-title">
                    Color Details
                  </span>
                </div>
                <div className="hk-mobile-color-selection-details-fields">
                  {attributes.map((attr, i) => (
                    <div
                      key={`attr-${i}`}
                      className="hk-mobile-color-selection-details-field"
                    >
                      <span className="hk-mobile-color-selection-details-field-label">
                        {attr.label}
                      </span>
                      <div className="hk-mobile-color-selection-details-field-value">
                        <span>{attr.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </IonContent>
          {isEditing ? (
            <IonToolbar className="hk-digital-item-toolbar hk-digital-item-toolbar-edit">
              <div className="hk-digital-item-detail-edit-actions">
                <IonButton
                  expand="block"
                  fill="outline"
                  className="hk-digital-item-detail-edit-cancel"
                  disabled={updateProductLoading}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </IonButton>
                <IonButton
                  expand="block"
                  color="primary"
                  strong={true}
                  disabled={updateProductLoading}
                  onClick={saveEdits}
                >
                  {updateProductLoading ? <IonSpinner name="dots" /> : 'Save'}
                </IonButton>
              </div>
            </IonToolbar>
          ) : (
            <IonToolbar className="hk-mobile-color-selection-details-footer">
              <IonButton
                expand="block"
                color="primary"
                strong={true}
                onClick={close}
              >
                Done
              </IonButton>
            </IonToolbar>
          )}
        </>
      )}
    </IonPage>
  );
};

export default ColorSelectionDetailsMobile;
